import { Box, BoxProps, Button, Input, Loader, useTheme } from '@ytl/common-web';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as Envelope } from '../assets/envelope.svg';
import { ReactComponent as ErrorIcon } from '../assets/error.svg';
import { useLanguage } from '../contexts/languageContext';
import useRecaptcha from '../modules/useRecaptcha';
import { RecaptchaPortal } from '../shared/components/RecaptchaPortal';
import { getConfig } from '../shared/data/config';
import { PasswordResource } from '../shared/resource/password.resource';
import { determineAppMode } from '../shared/utils/appMode';
import { isValidEmail } from '../shared/utils/emailValidator';
import { searchOnEnter } from '../shared/utils/searchOnEnter';
import { useQueryParams } from '../shared/utils/useQueryParams';

enum ForgottenPasswordState {
  NA,
  Success,
  Failed,
}

export const ForgottenPasswordPage = () => {
  const { translation } = useLanguage();
  const queryParams = useQueryParams();
  const [forgottenPasswordState, setForgottenPasswordState] = useState(ForgottenPasswordState.NA);
  const [isLoading, setLoading] = useState(false);
  const [emailAddress, setEmailAddress] = useState((queryParams.get('email') || '').trim());
  const [validationErrorList, setValidationErrorList] = useState<Array<string>>([]);
  //const linkParams = useLinkParams();
  const isB2BMode = determineAppMode() === 'b2b';
  const { isWebView } = useTheme();

  const resetWithEmail = (email: string) => {
    setForgottenPasswordState(ForgottenPasswordState.NA);
    setEmailAddress(email);
  };

  const tryAgainClicked = useCallback(() => {
    resetWithEmail(emailAddress);
  }, [emailAddress]);

  const query = useQueryParams();

  const {
    canContinueByCaptcha,
    needsCaptcha,
    recaptchaRef,
    recaptchaForceRender,
    recaptchaSiteKey,
    recaptchaLang,
    handleRecaptcha,
    captchaToken,
    captchaWidgetReset,
    recaptchaInvisible,
    executeInvisibleCaptcha,
  } = useRecaptcha();

  const waitingForCaptchaRef = useRef(false);

  const buttonDisabled = useMemo(() => {
    if (recaptchaInvisible) {
      return isLoading || !emailAddress;
    }
    return isLoading || !emailAddress || !canContinueByCaptcha;
  }, [emailAddress, canContinueByCaptcha, isLoading, recaptchaInvisible]);

  const sendResetEmail = useCallback(async () => {
    if (!isValidEmail(emailAddress)) {
      setValidationErrorList([translation.forgottenPassword.error.invalidEmail]);
      return;
    }
    setValidationErrorList([]);

    if (needsCaptcha && recaptchaInvisible && !captchaToken) {
      waitingForCaptchaRef.current = true;
      await executeInvisibleCaptcha();
      return;
    }

    waitingForCaptchaRef.current = false;

    try {
      setLoading(true);
      await PasswordResource.sendForgottenPasswordEmail(emailAddress, captchaToken);
      captchaWidgetReset();
      setForgottenPasswordState(ForgottenPasswordState.Success);
    } catch (err) {
      captchaWidgetReset();
      setForgottenPasswordState(ForgottenPasswordState.Failed);
    } finally {
      setLoading(false);
    }
  }, [
    emailAddress,
    translation,
    captchaToken,
    captchaWidgetReset,
    needsCaptcha,
    recaptchaInvisible,
    executeInvisibleCaptcha,
  ]);

  useEffect(() => {
    if (waitingForCaptchaRef.current && captchaToken) {
      waitingForCaptchaRef.current = false;
      sendResetEmail();
    }
  }, [captchaToken, sendResetEmail]);

  const boxConfig: Omit<BoxProps, 'children'> = useMemo(() => {
    if (forgottenPasswordState === ForgottenPasswordState.NA) {
      const button = (
        <Button
          disabled={buttonDisabled}
          onClick={sendResetEmail}
          aria-label={translation.forgottenPassword.send}
        >
          <Loader isLoading={isLoading} />
          {isWebView ? translation.forgottenPassword.sendApp : translation.forgottenPassword.send}
        </Button>
      );

      return {
        header: translation.forgottenPassword.header(query.has('lock')),
        subHeader: translation.forgottenPassword.subHeader,
        action: needsCaptcha ? (
          <>
            <RecaptchaPortal
              recaptchaRef={recaptchaRef}
              sitekey={recaptchaSiteKey}
              hl={recaptchaLang}
              onChange={handleRecaptcha}
              className={recaptchaInvisible ? undefined : 'password-page--recaptcha'}
              asyncScriptOnLoad={recaptchaForceRender}
              size={recaptchaInvisible ? 'invisible' : undefined}
            />
            {button}
          </>
        ) : (
          button
        ),
      };
    }
    if (forgottenPasswordState === ForgottenPasswordState.Success) {
      return {
        icon: <Envelope className={'icon'} />,
        header: translation.forgottenPassword.success.header,
        subHeaderHTML: translation.forgottenPassword.success.subHeader,
        action: (
          <Button
            variant={isWebView ? 'text-undecorated' : 'outlined'}
            onClick={() => {
              //const link = makeLink(getConfig().yoloPasswordForgetBacktoLoginUrl, linkParams);
              if (isWebView) {
                window.location.href = getConfig().closeWebViewUrl;
              } else if (isB2BMode) {
                window.location.href = getConfig().backToB2bAppUrl;
              } else {
                window.location.href = getConfig().mainYettelUrl;
              }
            }}
          >
            {isWebView
              ? translation.forgottenPassword.success.backToYettelApp
              : translation.forgottenPassword.success.backToYettel}
          </Button>
        ),
      };
    }

    return {
      icon: <ErrorIcon className={'icon icon--error'} />,
      headerHTML: translation.forgottenPassword.error.header,
      subHeader: translation.forgottenPassword.error.subHeader,
      action: <Button onClick={tryAgainClicked}>{translation.forgottenPassword.error.tryAgain}</Button>,
    };
  }, [
    forgottenPasswordState,
    isLoading,
    sendResetEmail,
    translation,
    tryAgainClicked,
    query,
    buttonDisabled,
    handleRecaptcha,
    needsCaptcha,
    recaptchaForceRender,
    recaptchaLang,
    recaptchaRef,
    recaptchaSiteKey,
    isB2BMode,
    recaptchaInvisible,
    isWebView,
  ]);

  return (
    <div className="password-page password-page--forgotten-password">
      <Box {...boxConfig}>
        {forgottenPasswordState === ForgottenPasswordState.NA && (
          <>
            <Input
              label={translation.forgottenPassword.emailLabel}
              name={'email'}
              type={'email'}
              inputMode={'email'}
              value={emailAddress}
              required={true}
              placeholder={'e-mail@gmail.com'}
              onChange={e => setEmailAddress(e.target.value)}
              errorList={validationErrorList}
              disabled={isLoading}
              onKeyUp={searchOnEnter(() => sendResetEmail())}
            />
          </>
        )}
        {forgottenPasswordState === ForgottenPasswordState.Success && (
          <>
            <Button
              variant={'text'}
              className="button--no-padding"
              onClick={() => {
                setForgottenPasswordState(ForgottenPasswordState.NA);
                setEmailAddress(emailAddress);
              }}
            >
              {translation.forgottenPassword.success.resend}
            </Button>
          </>
        )}
      </Box>
    </div>
  );
};
